import { ReactNode } from 'react';
import { AuthProvider } from './AuthContext';
import { OrganizationProvider } from './OrganizationContext';
import { PermissionProvider } from './PermissionContext';
import { NavigationProvider } from './NavigationContext';
import { FeatureFlagsProvider } from './FeatureFlagsContext';
import { ViewAsProvider } from './ViewAsContext';

/**
 * RoleContext - Backward Compatibility Wrapper
 * Phase 3 (P2) - Architectural Refactoring Complete + TanStack Query Migration
 *
 * This file now wraps 6 separated contexts instead of containing all logic:
 * - AuthContext: User authentication (userId, email, name)
 * - OrganizationContext: Organization management (orgs, selectedOrg, selectOrg) [TanStack Query]
 * - ViewAsContext: Owner Mode functionality (viewAsRole, setViewAsRole) [NEW]
 * - PermissionContext: Permission checks (globalRole, projectRoles, can*) [TanStack Query]
 * - NavigationContext: UI navigation (getLandingPage, getVisibleScreens)
 * - FeatureFlagsContext: Feature flag management (isEnabled) [TanStack Query]
 *
 * The old API is preserved through RoleAdapter, so existing code continues to work.
 */

/**
 * RoleProvider - Composite Provider
 * Wraps all 6 separated context providers
 * Maintains backward compatibility with existing code
 */
export function RoleProvider({ children }: { children: ReactNode }) {
  return (
    <AuthProvider>
      <OrganizationProvider>
        <ViewAsProvider>
          <PermissionProvider>
            <NavigationProvider>
              <FeatureFlagsProvider>{children}</FeatureFlagsProvider>
            </NavigationProvider>
          </PermissionProvider>
        </ViewAsProvider>
      </OrganizationProvider>
    </AuthProvider>
  );
}

/**
 * useRole - Backward Compatibility Hook
 * Re-exports the adapter hook that composites all 5 contexts
 * Existing components can continue using useRole() without changes
 */
export { useRole } from './RoleAdapter';

// Also export individual hooks for components that want to use them directly
export { useAuthUser } from './AuthContext';
export { useOrganization } from './OrganizationContext';
export { useViewAs } from './ViewAsContext';
export { usePermissions } from './PermissionContext';
export { useNavigation } from './NavigationContext';
export { useFeatureFlags } from './FeatureFlagsContext';
